import { useEffect, useState } from 'react';
import { PreviewIframeMessage, previewMessageBusSubject, createPreviewMessageEventHandler } from '@shared/utils/previewMessageBus';
import { isInIframe } from '@shared/utils/isInIframe';
import globalWindow from '@shared/core/globals';
import { DesignLayoutType, WebsiteHeaderPresentationSettingsFragment } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import equals from 'deep-equal';

type WebsiteHeaderUpdatesListenerOverrides = {
  activeHeaderPresentationLayout: Maybe<WebsiteHeaderPresentationSettingsFragment>;
  websiteLayout: Maybe<DesignLayoutType>;
  eventDisplayName: Maybe<string>;
  greetingMessage: Maybe<string>;
};

type UseWebsiteHeaderUpdatesListenerArgs = {
  initialValues: WebsiteHeaderUpdatesListenerOverrides;
};

const useWebsiteHeaderUpdatesListener = (args: UseWebsiteHeaderUpdatesListenerArgs) => {
  const { initialValues } = args;
  const isPreviewing = isInIframe();

  // Opting to store override in memory as opposed to refetching the query
  const [overrides, setOverrides] = useState<WebsiteHeaderUpdatesListenerOverrides>(() => {
    return {
      activeHeaderPresentationLayout: initialValues.activeHeaderPresentationLayout,
      websiteLayout: initialValues.websiteLayout,
      eventDisplayName: initialValues.eventDisplayName,
      greetingMessage: initialValues.greetingMessage,
    };
  });

  const processMessage = useEventCallback((message: PreviewIframeMessage) => {
    switch (message.action) {
      case 'fromWebsiteDesignerPayload': {
        const updatedPresentationLayout = message.value.eventDesign.activeWebsiteHeaderPresentationLayout;
        const updatedWebsiteLayout = message.value.eventDesign.websiteLayout.layoutType;
        if (!equals(updatedPresentationLayout, overrides.activeHeaderPresentationLayout) || updatedWebsiteLayout !== overrides.websiteLayout) {
          setOverrides(currentValue => ({
            ...currentValue,
            websiteLayout: updatedWebsiteLayout,
            activeHeaderPresentationLayout: updatedPresentationLayout,
          }));
        }
        break;
      }
      case 'fromList':
        message.value.forEach(messageValue => {
          switch (messageValue.key) {
            case 'eventDisplayName':
              setOverrides(currentValue => ({
                ...currentValue,
                eventDisplayName: messageValue.value,
              }));
              break;
            case 'greetings':
              setOverrides(currentValue => ({
                ...currentValue,
                greetingMessage: messageValue.value,
              }));
              break;
          }
        });
        break;
    }
  });

  useEffect(() => {
    if (!isPreviewing) {
      return;
    }

    const previewSub = previewMessageBusSubject.subscribe({
      next: action => {
        if (action.type === 'sendMessage') {
          processMessage(action.payload.message);
        }
      },
    });

    const receiveMessage = createPreviewMessageEventHandler(processMessage);
    globalWindow.addEventListener?.('message', receiveMessage, false);

    return () => {
      previewSub?.unsubscribe();
      globalWindow.removeEventListener?.('message', receiveMessage, false);
    };
  }, [isPreviewing, processMessage]);

  return { overrides };
};

export default useWebsiteHeaderUpdatesListener;
